// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';

import { spacing, fontSize, fontWeight } from 'presentations/views/styles';
import Dialog from 'presentations/views/components/atoms/Dialog';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import { black, gray01, gray04 } from 'presentations/views/styles/color';
import { ViewerItem } from 'presentations/api/schema/user';
import { usePlanSwitchForm } from './hooks';
import { DateTime, formatToDate, getDateTime } from 'infras/datetime';
import { formatPrice } from 'presentations/views/helpers/formatter';
import Skeleton from '../../atoms/Skeleton';
import {
  getPlanName,
  IntervalType,
  PlanId,
} from 'domains/models/subscription/planId';
import {
  latestPlanVersion,
  PlanVersion,
} from 'domains/models/subscription/planVersion';
import ConfirmationDialog from '../../molecules/ConfirmationDialog';

interface Props {
  viewer: ViewerItem;
  planId: PlanId | null;
  planVersion: PlanVersion | null;
  intervalType: IntervalType;
  isOpen: boolean;
  onCancel?: any;
  schedulePlanId: PlanId | null;
  schedulePlanVersion: PlanVersion | null;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = usePlanSwitchForm({
    viewer: props.viewer,
    planId: props.planId,
    intervalType: props.intervalType,
  });

  const adminSubscription = state.adminSubscription;
  const newSubscriptionstartsAt = getDateTime(
    state.previewInvoiceItem?.upcomingPeriodStart! * 1000,
  );

  if (props.schedulePlanId) {
    return (
      <ConfirmationDialog
        isOpen={props.isOpen}
        onSubmit={props.onCancel}
        submitLabel={t('OK')}
        title={t('Pay plan switch')}
        text={t('Pay plan required downgrade', {
          planName: getPlanName(props.planId!, props.planVersion),
        })}
      />
    );
  }
  return (
    <StyledDialog
      isOpen={props.isOpen}
      onClose={props.onCancel}
      title={t('Pay plan switch')}
      isFillWidth
      actions={
        <DialogSubmitField
          isLoading={state.isInitLoading || state.isLoading}
          onSubmit={actions.onSubmit}
          onCancel={props.onCancel}
          isDisabled={state.isInitLoading}
          submitLabel={t('Submit')}
        />
      }
    >
      <Block>
        <PreviewContainer>
          {state.isInitLoading ? (
            <>
              <PreviewItem>
                <Skeleton width="260px" height="16px" />
              </PreviewItem>
              <PreviewItem>
                <Skeleton width="150px" height="16px" />
                <Skeleton width="54px" height="16px" />
              </PreviewItem>
              <PreviewItem>
                <Skeleton width="150px" height="16px" />
                <Skeleton width="54px" height="16px" />
              </PreviewItem>
              <PreviewItem>
                <Skeleton width="150px" height="16px" />
                <Skeleton width="54px" height="16px" />
              </PreviewItem>
              <PreviewItem>
                <Skeleton width="150px" height="16px" />
                <Skeleton width="54px" height="16px" />
              </PreviewItem>
            </>
          ) : (
            <>
              <PreviewItem>{`${t('Amount')}（${getPlanName(
                props.planId!,
                latestPlanVersion,
              )}/${
                props.intervalType === IntervalType.Annually
                  ? t('Annually')
                  : t('Monthly')
              }）`}</PreviewItem>
              <PreviewItem>
                <span>{t('Start date')}</span>
                <span>
                  {formatToDate(
                    newSubscriptionstartsAt,
                    state.userProfile.timeZone,
                  )}
                </span>
              </PreviewItem>
              <PreviewItem>
                <span>{t('Seat count')}</span>
                <span>{adminSubscription?.quantity || ''}</span>
              </PreviewItem>
              {state.previewInvoiceItem &&
                state.previewInvoiceItem.prorationAmount !== 0 && (
                  <PreviewItem>
                    <span>{t('Proration')}</span>
                    <span>
                      {state.previewInvoiceItem &&
                        state.previewInvoiceItem.prorationAmount !== 0 && (
                          <>
                            {formatPrice(
                              state.previewInvoiceItem.prorationAmount +
                                state.previewInvoiceItem.prorationTaxAmount,
                              state.previewInvoiceItem.currency,
                            )}
                          </>
                        )}
                    </span>
                  </PreviewItem>
                )}
              <PreviewItem>
                <span>
                  {state.previewInvoiceItem &&
                  state.previewInvoiceItem.intervalType
                    ? state.previewInvoiceItem.intervalType ===
                      IntervalType.Annually
                      ? t('Annually amount')
                      : t('Monthly amount')
                    : ''}
                  （{t('Next cycle')}）
                </span>
                <span>
                  {state.previewInvoiceItem &&
                    formatPrice(
                      state.previewInvoiceItem.basicAmount +
                        state.previewInvoiceItem.basicTaxAmount,
                      state.previewInvoiceItem.currency,
                    )}
                </span>
              </PreviewItem>
            </>
          )}
        </PreviewContainer>
      </Block>
      <Description isPreview={true}>{t('preview description')}</Description>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: 400px;
  }
`;
const Block = styled.div`
  margin-bottom: ${spacing(24)};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const Description = styled.div<{
  isPreview: boolean;
}>`
  font-size: ${fontSize(14)};
  margin: ${spacing(24)} 0;
  color: ${(props) => (props.isPreview ? gray01(props) : black(props))};
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${spacing(24)};
`;
const PreviewItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${fontSize(14)};
  border-bottom: 1px solid ${gray04};
  padding: ${spacing(12)} 0;
  :first-of-type {
    font-weight: ${fontWeight('bold')};
    border-top: 1px solid ${gray04};
  }
`;
